import React, { useEffect, useState } from "react";
import styles from "./styles.module.scss";
import Modal from "../../../components/modal";
import Select from "../../../components/select";
import { AddUpdateMcc, getActiveBanks, ListOfPdfs } from "../../../../api";
import { Formik } from "formik";
import * as Yup from "yup";
import TextInput from "../../../components/form/text-input";
import FancyRadioInput from "../../../components/form/fancy-radio-input";
import TagInput from '../../../components/form/tag-input'
import ButtonIcon from "../../../components/ButtonIcon/ButtonIcon";
import toast from "react-hot-toast";
import useMccStore from "../../../stores/mcc";

const schema = Yup.object().shape({
  mcc: Yup.number()
    .required('* MCC is required')
    .positive('* MCC must be a positive number')
    .integer('* MCC must be a number'),
  category: Yup.string().required("* Category is required"),
  description: Yup.string().required("* Description is required"),
  banks: Yup.array().min(1, "* Please select at least 1 bank"),
  addendums: Yup.array().min(1, "* Please select at least 1 addendum"),
});


const AddAndEditMCC = ({
  isModalOpen,
  closeModal,
  getMCCList,
}) => {
  const [is_submitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    mcc: "",
    category: "",
    description: "",
    riskType: "low_risk",
    banks: [],
    addendums: [],
  });

  const [activeBanks, setActiveBanks] = useState([]);
  const [addendumsOptions, setAddendumsOptions] = useState([]);

  const setRecordToEdit = useMccStore((state) => state.setRecordToEdit);
  const recordToEdit = useMccStore((state) => state.recordToEdit);
  const [errors, setError] = useState({});

  const convertValidationErrors = (errors) => {
    if (!errors || typeof errors !== 'object') {
      return {};
    }
    const convertedErrors = {};
    for (const [key, value] of Object.entries(errors)) {
      convertedErrors[key] = Array.isArray(value) ? value[0] : value;
    }
    return convertedErrors;
  };

  const onSubmit = async (values) => {
    const toastId = toast.loading('Loading...');

    let form_data = {
      mcc: values.mcc,
      category: values.category,
      description: values.description,
      risk: values.riskType,
      bank: values.banks,
      addendums: values.addendums,
    }

    if (recordToEdit) {
      form_data.id = recordToEdit.id
    }

    try {
      const response = await AddUpdateMcc(form_data);
      if (response.success) {
        toast.success(`MCC ${recordToEdit ? "updated" : "added"} successfully`, { id: toastId });
        getMCCList();
        closeModal();
      } else {
        toast.error("Something went wrong", { id: toastId });
        getMCCList();
        closeModal();
      }
    } catch (error) {
      const convertedErrors = convertValidationErrors(error?.data?.errors);
      setError(convertedErrors);
      toast.error("Validation error", { id: toastId });
    }

  };

  const onClose = () => {
    reset();
    closeModal();
  };

  const reset = () => {
    setIsSubmitted(false);
    setFormData({
      mcc: "",
      category: "",
      description: "",
      riskType: "low_risk",
      banks: [],
      addendums: [],
    });
    setRecordToEdit(null);
  }

  useEffect(() => {
    if (isModalOpen) {
      const fetchBanks = async () => {
        try {
          const res = await getActiveBanks();

          if (res.status) {
            const banks = res.data.map((item) => ({
              value: item.id,
              label: item.name,
            }));
            setActiveBanks(banks);
          }
        } catch (error) {
          console.log("error :>> ", error);
          setActiveBanks([]);
        }
      };

      const fetchAddeddums = async () => {
        try {
          const res = await ListOfPdfs();

          if (res.data.addendums_names.length) {
            const addeddums = res.data.addendums_names.map((item) => ({
              value: item,
              label: item,
            }));

            setAddendumsOptions(addeddums);
          }
        } catch (error) {
          console.log("error :>> ", error);
          setAddendumsOptions([]);
        }
      };

      fetchBanks();
      fetchAddeddums();
    }
  }, [isModalOpen]);

  useEffect(() => {
    if (recordToEdit) {
      setFormData({
        mcc: recordToEdit.mcc,
        category: recordToEdit.category,
        description: recordToEdit.description,
        riskType: recordToEdit.risk,
        banks: recordToEdit.bank,
        addendums: recordToEdit.addendums,
      });
    }
  }, [recordToEdit])

  return (
    <Modal
      isOpen={isModalOpen}
      onClose={onClose}
      header={`${recordToEdit ? "Edit" : "Add"} MCC`}
    >

      <Formik
        enableReinitialize
        validationSchema={schema}
        onSubmit={onSubmit}
        initialValues={formData}
        validateOnBlur={true}
        validateOnChange={true}
        initialErrors={errors}
      >
        {({
          handleSubmit,
          handleChange,
          values,
          touched,
          errors,
          setFieldValue,
          isSubmitting,
        }) => (
          <form onSubmit={handleSubmit} autoComplete="off" className={styles.formContainer} >
            <div className={`${styles.grid} ${styles.gridCols2} ${styles.gap1}`}>
              <TextInput
                label={'MCC'}
                id={'mcc'}
                name={'mcc'}
                placeholder={'MCC'}
                value={values.mcc}
                onChange={handleChange}
                error={errors.mcc}
                errored={errors.mcc}
                touched={touched.mcc}
                type="number"
              />

              <TextInput
                label={'Category'}
                id={'category'}
                name={'category'}
                placeholder={'Category'}
                value={values.category}
                onChange={handleChange}
                error={errors.category}
                errored={errors.category}
                touched={touched.category}
                type="text"
              />

            </div>

            <div className={`${styles.grid} ${styles.gridCols1} ${styles.gap1}`}>
              <TextInput
                label={'Description'}
                id={'description'}
                name={'description'}
                placeholder={'Description'}
                value={values.description}
                onChange={handleChange}
                error={errors.description}
                errored={errors.description}
                touched={touched.description}
                type="text"
              />
            </div>

            <div className={styles.formGroup}  >
              <p className={styles.heading} >Risk Type</p>

              <div className={styles.riskTypeRadioContainer} >
                <FancyRadioInput
                  id={'low'}
                  name={'riskType'}
                  label={'Low'}
                  value={'low_risk'}
                  checked={values.riskType === 'low_risk'}
                  onChange={handleChange}
                />

                <FancyRadioInput
                  id={'high'}
                  name={'riskType'}
                  label={'High'}
                  value={'high_risk'}
                  checked={values.riskType === 'high_risk'}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className={styles.formGroup}  >
              <p className={styles.heading} >Banks</p>

              <div className={`${styles.grid} ${styles.gridCols1} ${styles.gap1}`} >
                <TagInput
                  id={'banks'}
                  name={'banks'}
                  options={activeBanks}
                  value={values.banks || []}
                  onChange={(value) => {
                    setFieldValue('banks', value);
                  }}
                  error={errors.banks}
                  errored={errors.banks}
                  touched={touched.banks}
                  placeholder={'Search'}
                />
              </div>
            </div>

            <div className={styles.formGroup}  >
              <p className={styles.heading} >Addendums</p>

              <div className={`${styles.grid} ${styles.gridCols1} ${styles.gap1}`} >
                <TagInput
                  id={'addendums'}
                  name={'addendums'}
                  options={addendumsOptions}
                  value={values.addendums || []}
                  onChange={(value) => {
                    setFieldValue('addendums', value);
                  }}
                  error={errors.addendums}
                  errored={errors.addendums}
                  touched={touched.addendums}
                  placeholder={'Search'}
                />
              </div>
            </div>

            <div className={styles.footer} >
              <ButtonIcon
                textColor="#254FE2"
                bgColor={'white'}
                type={'button'}
                onClick={onClose}
              >
                Cancel
              </ButtonIcon>

              <ButtonIcon
                textColor="#FFFFFF"
                bgColor={'#4C70EF'}
                type="submit"
              >
                Save
              </ButtonIcon>
            </div>

          </form>
        )}
      </Formik>

    </Modal>
  );
};

export default AddAndEditMCC;
